import { z } from 'zod'
import type { SiteChangeEntity } from '../enums/site-change.enum.js'
import {
  backflowTypeSchema,
  headTypeSchema,
  irrigationEquipmentKindSchema,
  pipeSizeSchema,
  sensorTypeSchema,
  waterSourceKindSchema,
  wellComponentTypeSchema,
} from '../enums/irrigation.enum.js'
import { recordedGeoPointSchema, siteBoundarySchema } from './geo.dto.js'

/*
 * The tracked fields of one site record, as they are stored on a change and
 * sent on the wire. `SITE_RECORD_FIELDS` says which fields are tracked;
 * these schemas say what a value of each one is. The two stay the same set
 * of keys (`SITE_SNAPSHOT_KEYS_MATCH_RULES`).
 *
 * A full snapshot is what a record looks like when it is added or removed.
 * A patch is the fields a change actually touched. Unknown keys are dropped:
 * a snapshot written by an older build may name a field we no longer track,
 * and that must not make the change unreadable. A known field of the wrong
 * type fails the patch. The on-wire schemas turn that failure into null, so
 * one bad side of one old change does not blank a site's history or fail
 * the request that loads it. A date or an id that is wrong still fails,
 * because those are the change itself, not a detail of it.
 */

/** One zone's heads of one kind, as a snapshot stores the count. */
export const zoneHeadCountSnapshotSchema = z.object({
  type: headTypeSchema,
  count: z.number().int().nonnegative(),
})

export const boundarySnapshotSchema = z.object({
  boundary: siteBoundarySchema.nullable(),
})

export const areaSnapshotSchema = z.object({
  name: z.string(),
  boundary: siteBoundarySchema.nullable(),
})

export const treeSnapshotSchema = z.object({
  label: z.string(),
  speciesId: z.string().nullable(),
  ...recordedGeoPointSchema.shape,
  dbhInches: z.number().nullable(),
})

export const waterSourceSnapshotSchema = z.object({
  kind: waterSourceKindSchema,
  label: z.string().nullable(),
  ...recordedGeoPointSchema.shape,
  meterSize: pipeSizeSchema.nullable(),
  horsepower: z.number().nullable(),
})

export const timerSnapshotSchema = z.object({
  label: z.string().nullable(),
  ...recordedGeoPointSchema.shape,
  partUuid: z.string().nullable(),
  waterSourceId: z.string().nullable(),
})

export const zoneSnapshotSchema = z.object({
  timerId: z.string(),
  number: z.number().int(),
  boundary: siteBoundarySchema.nullable(),
  headCounts: z.array(zoneHeadCountSnapshotSchema),
})

export const equipmentSnapshotSchema = z.object({
  kind: irrigationEquipmentKindSchema,
  name: z.string().nullable(),
  ...recordedGeoPointSchema.shape,
  partUuid: z.string().nullable(),
  decoderUuid: z.string().nullable(),
  zoneId: z.string().nullable(),
  timerId: z.string().nullable(),
  waterSourceId: z.string().nullable(),
  size: pipeSizeSchema.nullable(),
  backflowType: backflowTypeSchema.nullable(),
  serialNumber: z.string().nullable(),
  sensorType: sensorTypeSchema.nullable(),
  wellComponentType: wellComponentTypeSchema.nullable(),
  /** The valve box it sits in. */
  boxId: z.string().nullable(),
  /** A valve box's splices. */
  spliceCount: z.number().int().nonnegative().nullable(),
})

/** Every tracked field of one kind of record. */
export const siteSnapshotSchemas = {
  BOUNDARY: boundarySnapshotSchema,
  AREA: areaSnapshotSchema,
  TREE: treeSnapshotSchema,
  WATER_SOURCE: waterSourceSnapshotSchema,
  TIMER: timerSnapshotSchema,
  ZONE: zoneSnapshotSchema,
  EQUIPMENT: equipmentSnapshotSchema,
} as const satisfies Record<SiteChangeEntity, z.ZodType>

export type SiteRecordSnapshotOf<E extends SiteChangeEntity> = z.infer<
  (typeof siteSnapshotSchemas)[E]
>

/** The tracked fields a single change touched. Every key is optional. */
export const siteSnapshotPatchSchemas = {
  BOUNDARY: boundarySnapshotSchema.partial(),
  AREA: areaSnapshotSchema.partial(),
  TREE: treeSnapshotSchema.partial(),
  WATER_SOURCE: waterSourceSnapshotSchema.partial(),
  TIMER: timerSnapshotSchema.partial(),
  ZONE: zoneSnapshotSchema.partial(),
  EQUIPMENT: equipmentSnapshotSchema.partial(),
} as const satisfies Record<SiteChangeEntity, z.ZodType>

export type SiteSnapshotPatch<E extends SiteChangeEntity> = z.infer<
  (typeof siteSnapshotPatchSchemas)[E]
>

/**
 * A patch as it arrives from storage or the wire: the patch, or null when
 * there was none or it does not read as that kind of record.
 */
function onWire<Schema extends z.ZodType>(schema: Schema) {
  return schema.nullable().catch(null)
}

export const boundarySnapshotOnWire = onWire(siteSnapshotPatchSchemas.BOUNDARY)
export const areaSnapshotOnWire = onWire(siteSnapshotPatchSchemas.AREA)
export const treeSnapshotOnWire = onWire(siteSnapshotPatchSchemas.TREE)
export const waterSourceSnapshotOnWire = onWire(
  siteSnapshotPatchSchemas.WATER_SOURCE,
)
export const timerSnapshotOnWire = onWire(siteSnapshotPatchSchemas.TIMER)
export const zoneSnapshotOnWire = onWire(siteSnapshotPatchSchemas.ZONE)
export const equipmentSnapshotOnWire = onWire(
  siteSnapshotPatchSchemas.EQUIPMENT,
)
